import { useState, useContext, useMemo } from "react"
import firebase from "firebase"
import moment from "moment"
import { ChevronDown, ChevronRight } from "lucide-react"
import { db, auth } from "../../utils/firebase"
import { AlertsContext } from "../../utils/providers/alerts"
import { ModalContext } from "../../utils/providers/modal"
import { AuthContext } from "../../utils/providers/auth"
import "./commission-report-new.scss"

// UI components
import Tile from "../../components/structure/tile/tile"
import Title from "../../components/structure/title/title"
import Button from "../../components/ui/button/button"
import Table from "../../components/structure/table/table"
import Datepicker from "../../components/ui/datepicker/datepicker"
import Badge from "../../components/ui/badge/badge"
import exportToExcel from "./_utils/export"
import exportSettlePlan from "./_utils/settle-plan-export"

const BAND_5_RATE = 0.05
const BAND_6_5_RATE = 0.065
const BAND_8_RATE = 0.08

// New banded structure applies only to bookings booked from this date onward.
// Anything booked before this date is on the legacy flat-5% structure.
const BANDED_STRUCTURE_START_MS = moment("2026-05-01").startOf("day").valueOf()
const LEGACY_BAND = { key: "legacy", label: "5% (legacy)", rate: BAND_5_RATE }

const PAYMENT_LABELS = {
	FULL: "Full",
	FIRST_HALF: "First half",
	SECOND_HALF: "Second half",
	SETTLED: "Settled",
}

// Band derived from agent's % to target in the booking's BOOKED MONTH
function pickBand(percentToTarget, hasTarget) {
	if (!hasTarget) return { key: "none", label: "—", rate: null }
	if (percentToTarget > 120) return { key: "8", label: "8%", rate: BAND_8_RATE }
	if (percentToTarget > 105) return { key: "6_5", label: "6.5%", rate: BAND_6_5_RATE }
	return { key: "5", label: "5%", rate: BAND_5_RATE }
}

// Map our internal payment type to the legacy `payment_type` string stored on commission_logs
const PAYMENT_TYPE_STRING = {
	FIRST_HALF: "booking_month",
	SECOND_HALF: "travel_month",
	FULL: "full_payment",
}

// Returns the reason a row would be skipped at settle time (for the dry-run plan), or
// null if it should be written. `this_payment` is already remaining-based ("what is
// still owed this run"), so the rule is simply: skip anything that isn't payable.
function skipReason(row) {
	if (row.payment_type === "SETTLED") return "Already settled in full"
	if (row.this_payment < 0) return "Negative commission (shown, not settled)"
	if (!(row.this_payment > 0)) return "Nothing to pay (zero commission)"
	return null
}

export default function CommissionReportNew() {
	const [loading, setLoading] = useState(false)
	const [processing, setProcessing] = useState(false)
	const [settling, setSettling] = useState(false)

	const [startDate, setStartDate] = useState(null)
	const [endDate, setEndDate] = useState(null)

	const [rows, setRows] = useState([])
	const [periodLabel, setPeriodLabel] = useState("")
	const [expandedAgents, setExpandedAgents] = useState(new Set())

	const { pushAlert } = useContext(AlertsContext)
	const { showModal, dismissModal } = useContext(ModalContext)
	const { user } = useContext(AuthContext)

	const isAdmin = !!user?.is_admin
	const currentUid = auth.currentUser?.uid

	const fetchReport = async () => {
		if (!startDate || !endDate) return

		setLoading(true)
		setRows([])
		setExpandedAgents(new Set())

		try {
			const startMoment = moment(startDate).startOf("day")
			const endMoment = moment(endDate).endOf("day")

			const startTs = firebase.firestore.Timestamp.fromMillis(startMoment.valueOf())
			const endTs = firebase.firestore.Timestamp.fromMillis(endMoment.valueOf())

			// 1) Eligible agents (gates out non-commission users)
			const agentDocs = await db.collection("users").where("show_for.targets", "==", true).get()
			const agentById = {}
			agentDocs.forEach((doc) => {
				const data = doc.data()
				agentById[doc.id] = {
					id: doc.id,
					name: `${data.first_name || ""} ${data.last_name || ""}`.trim() || "Unknown",
				}
			})

			// 2) Two queries: bookings BOOKED in range, and bookings TRAVELLED in range.
			//    Merge so each unique booking carries flags for both conditions.
			const [bookedSnap, travelledSnap] = await Promise.all([
				db.collection("bookings").where("booked", ">=", startTs).where("booked", "<=", endTs).get(),
				db.collection("bookings").where("check_in", ">=", startTs).where("check_in", "<=", endTs).get(),
			])

			const merged = new Map()
			const addDoc = (doc, flag) => {
				const existing = merged.get(doc.id)
				if (existing) {
					existing[flag] = true
				} else {
					merged.set(doc.id, { id: doc.id, data: doc.data(), bookedInRange: false, travelledInRange: false, [flag]: true })
				}
			}
			bookedSnap.forEach((d) => addDoc(d, "bookedInRange"))
			travelledSnap.forEach((d) => addDoc(d, "travelledInRange"))

			// 3) Apply gates, derive booked-month, and collect filtered bookings
			const filteredBookings = []
			merged.forEach((entry) => {
				const data = entry.data
				const { margins, agent, booked, check_in, reference } = data
				if (!agent || !agentById[agent]) return
				// Non-admins only see their own bookings
				if (!isAdmin && agent !== currentUid) return
				if (!margins) return
				const { customer_price, profit } = margins
				if (!(customer_price > 0)) return
				// Keep loss-making (negative-margin) bookings: they belong on the report and their
				// negative commission nets against the agent's run total. Only drop missing/invalid margins.
				if (typeof profit !== "number" || !isFinite(profit)) return
				if (!booked || !check_in) return

				const bookedMs = booked.toMillis()
				const travelMs = check_in.toMillis()
				filteredBookings.push({
					id: entry.id,
					reference: reference || entry.id,
					agent_id: agent,
					booked_ms: bookedMs,
					travel_ms: travelMs,
					booked_month_key: moment(bookedMs).format("YYYY_MM"),
					profit: Number(profit) || 0,
					bookedInRange: entry.bookedInRange,
					travelledInRange: entry.travelledInRange,
					is_legacy: bookedMs < BANDED_STRUCTURE_START_MS,
				})
			})

			// 4) Compute agent's total booked-month margin for each unique booked-month appearing.
			//    Only needed for banded (non-legacy) bookings since legacy ones are flat 5%
			//    and don't depend on % to target.
			const uniqueBookedMonths = Array.from(new Set(filteredBookings.filter((b) => !b.is_legacy).map((b) => b.booked_month_key)))
			const monthlyMarginByAgent = {}

			await Promise.all(
				uniqueBookedMonths.map(async (monthKey) => {
					const monthMoment = moment(monthKey, "YYYY_MM")
					const monthStartTs = firebase.firestore.Timestamp.fromMillis(monthMoment.clone().startOf("month").valueOf())
					const monthEndTs = firebase.firestore.Timestamp.fromMillis(monthMoment.clone().endOf("month").valueOf())

					const monthBookings = await db.collection("bookings").where("booked", ">=", monthStartTs).where("booked", "<=", monthEndTs).get()

					monthlyMarginByAgent[monthKey] = {}
					monthBookings.forEach((doc) => {
						const data = doc.data()
						const { margins, agent } = data
						if (!agent || !agentById[agent]) return
						if (!margins) return
						const { customer_price, profit } = margins
						if (!(customer_price > 0)) return
						// Net losses in too, so the agent's monthly margin (and % to target) reflects
						// real performance rather than only their winning bookings.
						if (typeof profit !== "number" || !isFinite(profit)) return
						monthlyMarginByAgent[monthKey][agent] = (monthlyMarginByAgent[monthKey][agent] || 0) + Number(profit)
					})
				})
			)

			// 5) Look up target archives for every unique (agent, booked-month) tuple,
			//    falling back to the agent's current monthly target. Legacy bookings skipped.
			const uniqueAgentMonths = new Set()
			filteredBookings.forEach((b) => {
				if (!b.is_legacy) uniqueAgentMonths.add(`${b.agent_id}__${b.booked_month_key}`)
			})

			const currentTargetByAgent = {}
			await Promise.all(
				Object.keys(agentById).map(async (agentId) => {
					const snap = await db.doc(`targets/${agentId}`).get()
					currentTargetByAgent[agentId] = snap.exists ? Number(snap.data().monthly) || 0 : 0
				})
			)

			const targetByAgentMonth = {}
			await Promise.all(
				Array.from(uniqueAgentMonths).map(async (tuple) => {
					const sep = tuple.indexOf("__")
					const agentId = tuple.substring(0, sep)
					const monthKey = tuple.substring(sep + 2)

					const snap = await db.doc(`targets/${agentId}/archives/${monthKey}`).get()
					const archived = snap.exists ? Number(snap.data().monthly) || 0 : null

					if (!targetByAgentMonth[agentId]) targetByAgentMonth[agentId] = {}
					targetByAgentMonth[agentId][monthKey] = archived && archived > 0 ? archived : currentTargetByAgent[agentId] || 0
				})
			)

			// 5b) Fetch existing commission_logs for every booking in our filtered set so we
			//     can show "Commission paid so far" and dedupe by payment_type at settle time.
			const logResults = await Promise.all(filteredBookings.map((b) => db.collection(`bookings/${b.id}/commission_logs`).get()))
			const logsByBookingId = {}
			filteredBookings.forEach((b, i) => {
				const docs = logResults[i].docs
				const totalPaid = docs.reduce((sum, d) => sum + (Number(d.data().amount) || 0), 0)
				const types = new Set()
				docs.forEach((d) => {
					const t = d.data().payment_type
					if (t) types.add(t)
				})
				logsByBookingId[b.id] = { totalPaid, types }
			})

			// 6) Build per-booking rows with payment type + amount
			const builtRows = filteredBookings.map((b) => {
				let agentMonthlyMargin, target, hasTarget, pct, band
				if (b.is_legacy) {
					// Pre-banding: flat 5% regardless of target performance
					agentMonthlyMargin = 0
					target = 0
					hasTarget = false
					pct = 0
					band = LEGACY_BAND
				} else {
					agentMonthlyMargin = monthlyMarginByAgent[b.booked_month_key]?.[b.agent_id] || 0
					target = targetByAgentMonth[b.agent_id]?.[b.booked_month_key] || 0
					hasTarget = target > 0
					pct = hasTarget ? (agentMonthlyMargin / target) * 100 : 0
					band = pickBand(pct, hasTarget)
				}

				const fullCommission = band.rate != null ? b.profit * band.rate : 0
				const halfCommission = fullCommission / 2

				const logsForBooking = logsByBookingId[b.id] || { totalPaid: 0, types: new Set() }
				const paidSoFar = logsForBooking.totalPaid
				const paidTypes = logsForBooking.types

				// Payment type + amount are remaining-based and keyed off which milestones have
				// already been settled (the logged payment_types), NOT off the raw paid amount.
				// This keeps the report idempotent: re-running the same dates after a settle gives
				// the same answer instead of mis-reading an already-paid booking as "first half
				// done, second half still owed" (which silently halved the agent's commission).
				let payment_type
				let this_payment

				if (fullCommission <= 0) {
					// Loss-making (or zero) booking: keep existing behaviour - show the (negative)
					// commission for the matching window so it nets against the agent's run total.
					// These are never settled (skipReason drops anything <= 0).
					if (b.bookedInRange && b.travelledInRange) {
						payment_type = "FULL"
						this_payment = fullCommission
					} else if (b.bookedInRange) {
						payment_type = "FIRST_HALF"
						this_payment = halfCommission
					} else {
						payment_type = "SECOND_HALF"
						this_payment = halfCommission
					}
				} else {
					const remaining = Math.max(0, fullCommission - paidSoFar)
					const firstHalfDone = paidTypes.has("booking_month")
					const fullyDone = paidTypes.has("full_payment") || remaining < 0.01

					if (fullyDone) {
						// Already paid in full - nothing left to pay this (or any) run.
						payment_type = "SETTLED"
						this_payment = 0
					} else if (b.bookedInRange && b.travelledInRange) {
						// Booked and travelled inside the window - pay the full remainder now.
						payment_type = "FULL"
						this_payment = remaining
					} else if (b.travelledInRange) {
						// Travel-month window. If the first half was settled in the booked month,
						// pay the remainder as the second half. If it was NEVER settled the agent
						// would otherwise be left short, so pay everything still owed in one go.
						payment_type = firstHalfDone ? "SECOND_HALF" : "FULL"
						this_payment = remaining
					} else {
						// Booked-month window (travel is later). Pay the first half, unless it has
						// already been settled, in which case there's nothing to pay yet.
						payment_type = "FIRST_HALF"
						this_payment = firstHalfDone ? 0 : Math.min(halfCommission, remaining)
					}
				}

				return {
					booking_id: b.id,
					reference: b.reference,
					agent_id: b.agent_id,
					agent_name: agentById[b.agent_id].name,
					booked_ms: b.booked_ms,
					travel_ms: b.travel_ms,
					booked_month_key: b.booked_month_key,
					booked_month_label: moment(b.booked_ms).format("MMM YYYY"),
					profit: b.profit,
					agent_monthly_margin: agentMonthlyMargin,
					target,
					has_target: hasTarget,
					percent_to_target: pct,
					band_key: band.key,
					band_label: band.label,
					full_commission: fullCommission,
					payment_type,
					payment_label: PAYMENT_LABELS[payment_type],
					this_payment,
					is_legacy: b.is_legacy,
					commission_paid_so_far: logsForBooking.totalPaid,
					existing_payment_types: logsForBooking.types,
				}
			})

			builtRows.sort((a, b) => {
				const n = a.agent_name.localeCompare(b.agent_name)
				if (n !== 0) return n
				return a.booked_ms - b.booked_ms
			})

			setRows(builtRows)
			setPeriodLabel(`${startMoment.format("DD MMM YYYY")} – ${endMoment.format("DD MMM YYYY")}`)
		} catch (error) {
			console.error("Error fetching commission report:", error)
			pushAlert({
				type: "ERROR",
				title: "Report failed",
				body: "There was an error generating the commission report",
			})
			setRows([])
		} finally {
			setLoading(false)
		}
	}

	const handleExport = async () => {
		if (rows.length === 0) return
		setProcessing(true)
		try {
			const startMoment = moment(startDate)
			const endMoment = moment(endDate)
			const formattedDateTime = moment().format("YYYY_MM_DD_HH_mm")
			const fileName = `${startMoment.format("YYYY_MMMDD")}_to_${endMoment.format("YYYY_MMMDD")}_Commission_Report_${formattedDateTime}`
			await exportToExcel(rows, fileName, { periodLabel })
			pushAlert({
				type: "SUCCESS",
				title: "Report exported",
				body: "The commission report has been downloaded",
			})
		} catch (error) {
			console.error("Export error:", error)
			pushAlert({
				type: "ERROR",
				title: "Export failed",
				body: "There was an error exporting the commission report",
			})
		} finally {
			setProcessing(false)
		}
	}

	// Pre-compute which rows we'd actually write a log for (skips settled / non-payable rows)
	const eligibleForSettle = useMemo(() => rows.filter((row) => !skipReason(row)), [rows])

	const eligibleTotal = useMemo(() => eligibleForSettle.reduce((s, r) => s + r.this_payment, 0), [eligibleForSettle])
	const skippedCount = rows.length - eligibleForSettle.length

	const performSettle = async () => {
		setSettling(true)
		try {
			const results = await Promise.allSettled(
				eligibleForSettle.map(async (row) => {
					const newType = PAYMENT_TYPE_STRING[row.payment_type]
					const totalPaidToDate = (row.commission_paid_so_far || 0) + row.this_payment

					// Mirror the cloud function's new comparison field on the booking.
					// Writing it here makes commission_paid resolve correctly for banded bookings.
					await db.doc(`bookings/${row.booking_id}`).set({ commission_target: row.full_commission }, { merge: true })

					await db.collection(`bookings/${row.booking_id}/commission_logs`).add({
						amount: row.this_payment,
						created: firebase.firestore.FieldValue.serverTimestamp(),
						paid_by: auth.currentUser.uid,
						payment_type: newType,
						original_commission: row.full_commission,
						total_paid_to_date: totalPaidToDate,
					})
				})
			)
			const failures = results.filter((r) => r.status === "rejected").length
			const written = results.length - failures

			pushAlert({
				type: failures > 0 ? "ERROR" : "SUCCESS",
				title: failures > 0 ? "Settled with errors" : "Commission settled",
				body: `${written} log${written !== 1 ? "s" : ""} written${failures > 0 ? `, ${failures} failed` : ""}${skippedCount > 0 ? `, ${skippedCount} already settled` : ""}.`,
			})

			// Refresh report so the "Commission paid so far" + dedupe state reflects the writes
			await fetchReport()
		} catch (error) {
			console.error("Settle error:", error)
			pushAlert({
				type: "ERROR",
				title: "Settle failed",
				body: "There was an error writing commission logs",
			})
		} finally {
			setSettling(false)
		}
	}

	const handleDryRun = async () => {
		if (rows.length === 0) return
		setProcessing(true)
		try {
			const plan = rows.map((row) => {
				const reason = skipReason(row)
				return {
					action: reason ? "SKIP" : "WRITE",
					reason: reason || "",
					agent_id: row.agent_id,
					agent_name: row.agent_name,
					reference: row.reference,
					booked_ms: row.booked_ms,
					travel_ms: row.travel_ms,
					profit: row.profit,
					full_commission: row.full_commission,
					commission_paid_so_far: row.commission_paid_so_far || 0,
					payment_type_string: PAYMENT_TYPE_STRING[row.payment_type] || "",
					amount: row.this_payment,
				}
			})

			const startMoment = moment(startDate)
			const endMoment = moment(endDate)
			const formattedDateTime = moment().format("YYYY_MM_DD_HH_mm")
			const fileName = `${startMoment.format("YYYY_MMMDD")}_to_${endMoment.format("YYYY_MMMDD")}_Settle_DryRun_${formattedDateTime}`

			await exportSettlePlan(plan, fileName, { periodLabel })

			const writeCount = plan.filter((p) => p.action === "WRITE").length
			const skipCount = plan.length - writeCount

			pushAlert({
				type: "SUCCESS",
				title: "Dry run downloaded",
				body: `Plan shows ${writeCount} write${writeCount !== 1 ? "s" : ""} and ${skipCount} skip${skipCount !== 1 ? "s" : ""}. No data was written.`,
			})
		} catch (error) {
			console.error("Dry run error:", error)
			pushAlert({
				type: "ERROR",
				title: "Dry run failed",
				body: "There was an error generating the dry-run plan",
			})
		} finally {
			setProcessing(false)
		}
	}

	const handleSettleClick = () => {
		if (eligibleForSettle.length === 0) {
			pushAlert({
				type: "INFO",
				title: "Nothing to settle",
				body: skippedCount > 0 ? "Every booking in this report has already been settled for its matching half." : "There are no eligible bookings in this report.",
			})
			return
		}

		const agentCount = new Set(eligibleForSettle.map((r) => r.agent_id)).size
		const totalStr = `£${eligibleTotal.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
		const skipNote = skippedCount > 0 ? ` ${skippedCount} booking${skippedCount !== 1 ? "s" : ""} will be skipped (already settled).` : ""

		showModal({
			type: "ALERT",
			title: "Settle this run?",
			body: `${totalStr} across ${eligibleForSettle.length} booking${eligibleForSettle.length !== 1 ? "s" : ""} for ${agentCount} agent${agentCount !== 1 ? "s" : ""}.${skipNote}`,
			cancel: { label: "Cancel", action: () => null },
			next: {
				label: "Settle now",
				action: () => {
					dismissModal()
					performSettle()
				},
			},
		})
	}

	const formatGBP = (value) => {
		const n = value || 0
		const abs = Math.abs(n).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
		return n < 0 ? `-£${abs}` : `£${abs}`
	}
	const formatDate = (ms) => (ms ? moment(ms).format("DD MMM YYYY") : "—")

	// Group rows by agent
	const groupedByAgent = useMemo(() => {
		const groups = []
		const byAgent = {}
		rows.forEach((row) => {
			if (!byAgent[row.agent_id]) {
				const g = { agent_id: row.agent_id, agent_name: row.agent_name, bookings: [], total: 0 }
				byAgent[row.agent_id] = g
				groups.push(g)
			}
			byAgent[row.agent_id].bookings.push(row)
			byAgent[row.agent_id].total += row.this_payment
		})
		return groups
	}, [rows])

	const grandTotal = useMemo(() => groupedByAgent.reduce((s, g) => s + g.total, 0), [groupedByAgent])

	const toggleAgent = (agentId) => {
		setExpandedAgents((prev) => {
			const next = new Set(prev)
			if (next.has(agentId)) next.delete(agentId)
			else next.add(agentId)
			return next
		})
	}

	const expandAll = () => setExpandedAgents(new Set(groupedByAgent.map((g) => g.agent_id)))
	const collapseAll = () => setExpandedAgents(new Set())
	const allExpanded = groupedByAgent.length > 0 && expandedAgents.size === groupedByAgent.length

	return (
		<Tile fullPage={true}>
			<Title className="flex has-select-field">
				<h1>Commission Report</h1>
			</Title>

			<div className="page-filters extra-stats commission-report-filters">
				<Datepicker
					value={startDate}
					onSelect={(date) => setStartDate(date)}
					placeholder="Start date"
				/>

				<Datepicker
					value={endDate}
					onSelect={(date) => setEndDate(date)}
					placeholder="End date"
				/>

				<div className="commission-report-actions">
					<Button
						filters={true}
						disabled={!startDate || !endDate}
						label="Generate report"
						loadingText="Generating..."
						loading={loading}
						onClick={() => fetchReport()}
					/>

					{isAdmin && (
						<>
							<Button
								filters={true}
								disabled={rows.length === 0}
								label="Export to XLSX"
								loadingText="Exporting..."
								loading={processing}
								onClick={() => handleExport()}
							/>

							<div className="settle-wrap">
								<Button
									filters={true}
									disabled={rows.length === 0 || processing}
									label="Dry run"
									loadingText="Building plan..."
									loading={processing}
									onClick={() => handleDryRun()}
								/>

								<Button
									filters={true}
									disabled={eligibleForSettle.length === 0 || settling}
									label="Settle this run"
									loadingText="Settling..."
									loading={settling}
									onClick={() => handleSettleClick()}
								/>
							</div>
						</>
					)}
				</div>
			</div>

			<div className="commission-report-new">
				{rows.length > 0 && (
					<>
						<div className="report-summary">
							<div>
								<span className="summary-label">Period</span>
								<span className="summary-value">{periodLabel}</span>
							</div>
							<div>
								<span className="summary-label">Agents</span>
								<span className="summary-value">{groupedByAgent.length}</span>
							</div>
							<div>
								<span className="summary-label">Bookings</span>
								<span className="summary-value">{rows.length}</span>
							</div>
							<div>
								<span className="summary-label">Total to pay this run</span>
								<span className="summary-value summary-total">{formatGBP(grandTotal)}</span>
							</div>
							<div className="summary-actions">
								<button
									type="button"
									className="summary-link"
									onClick={() => (allExpanded ? collapseAll() : expandAll())}>
									{allExpanded ? "Collapse all" : "Expand all"}
								</button>
							</div>
						</div>

						<div className="agent-list">
							{groupedByAgent.map((group) => {
								const isOpen = expandedAgents.has(group.agent_id)
								return (
									<div
										key={group.agent_id}
										className={`agent-card ${isOpen ? "is-open" : ""}`}>
										<button
											type="button"
											className="agent-card-header"
											onClick={() => toggleAgent(group.agent_id)}>
											<span className="chevron">{isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}</span>
											<span className="agent-name">{group.agent_name}</span>
											<span className="meta">
												{group.bookings.length} booking{group.bookings.length !== 1 ? "s" : ""}
											</span>
											<span className="total">{formatGBP(group.total)}</span>
										</button>

										{isOpen && (
											<div className="agent-card-body">
												<Table
													className="booking-detail-table"
													headings={[
														"Booking ref",
														"Booked",
														"Travel",
														"Booking margin",
														"Target",
														"Agent margin booking month",
														"% to target",
														"Band",
														"Full commission",
														"Paid so far",
														"Payment",
														"This payment",
													]}>
													{group.bookings.map((row) => (
														<tr key={row.booking_id}>
															<td>
																<a
																	href={`/booking/${row.booking_id}`}
																	target="_blank"
																	rel="noreferrer"
																	className="booking-link">
																	{row.reference}
																</a>
															</td>
															<td>{formatDate(row.booked_ms)}</td>
															<td>{formatDate(row.travel_ms)}</td>
															<td>{formatGBP(row.profit)}</td>
															<td>
																{row.is_legacy ? (
																	<span className="muted">—</span>
																) : row.has_target ? (
																	formatGBP(row.target)
																) : (
																	<Badge
																		type="AMBER"
																		label="No target set"
																	/>
																)}
															</td>
															<td>{row.is_legacy ? <span className="muted">—</span> : formatGBP(row.agent_monthly_margin)}</td>
															<td>{row.is_legacy || !row.has_target ? <span className="muted">—</span> : `${row.percent_to_target.toFixed(2)}%`}</td>
															<td className="band-cell">{row.band_label}</td>
															<td>{formatGBP(row.full_commission)}</td>
															<td>{formatGBP(row.commission_paid_so_far)}</td>
															<td>
																<Badge
																	type={
																		row.payment_type === "SETTLED"
																			? "DEFAULT"
																			: row.payment_type === "FULL"
																			? "POSITIVE"
																			: row.payment_type === "FIRST_HALF"
																			? "INFO"
																			: "SPECIAL"
																	}
																	label={row.payment_label}
																/>
															</td>
															<td className="band-cell">{formatGBP(row.this_payment)}</td>
														</tr>
													))}
												</Table>
											</div>
										)}
									</div>
								)
							})}
						</div>
					</>
				)}

				{rows.length === 0 && !loading && <div className="report-empty">Pick a date range and click Generate report.</div>}
			</div>
		</Tile>
	)
}
